import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";

import { User } from "../users/User";
import { UserRoleEnum } from "../userRoles/UserRole";
import DepartmentsDropdown from "../departments/DepartmentsDropdown";
import UserRolesDropdown from "../userRoles/UserRoleDropdown";
import FormGroup from "../../common/components/form/FormGroup";
import TeamsDropdown from "../teams/TeamsDropdown";
import UsersDropdown from "./UsersDropdown";
import LocationsDropdown from "../locations/LocationsDropdown";

interface UserFormProps {
  user?: User;
  readOnly?: boolean;
  onSubmit?: (user: User) => Promise<void>;
}

export default function UserForm(props: UserFormProps) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<User>({ defaultValues: props.user });
  const [isLoading, setIsLoading] = useState(false);
  const watchedDepartmentId = watch("departmentId");
  const watchedUserRole = watch("role");
  const isAgent = watchedUserRole === UserRoleEnum.Agent;

  const onSubmit = async (data: User) => {
    setIsLoading(true);
    await props.onSubmit?.(data);
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid mb-2">
        {!props.readOnly && (
          <FormGroup className="col-12 gap-2" inline label="Manager aprobari" size="small">
            <Controller
              name="canApproveTickets"
              control={control}
              render={({ field }) => <InputSwitch checked={!!field.value} onChange={(e) => field.onChange(e.value)} />}
            ></Controller>
          </FormGroup>
        )}
        <FormGroup className={isAgent ? "col-4" : "col-12"} label="Rol" errorMessage={errors.role?.message} size="small">
          <Controller
            name="role"
            control={control}
            rules={{ required: "Rolul este obligatoriu" }}
            render={({ field }) => (
              <UserRolesDropdown
                value={field.value}
                onChange={(selectedValue) => field.onChange(selectedValue)}
                disabled={props.readOnly}
              />
            )}
          />
        </FormGroup>
        {isAgent && (
          <>
            <FormGroup className="col-4" label="Departament" errorMessage={errors.departmentId?.message} size="small">
              <Controller
                name="departmentId"
                control={control}
                rules={{ required: "Departamentul este obligatoriu" }}
                render={({ field }) => (
                  <DepartmentsDropdown
                    optionsType="all"
                    value={field.value}
                    onChange={(selectedValue) => {
                      field.onChange(selectedValue);
                      setValue("teamIds", []);
                    }}
                    disabled={props.readOnly}
                  />
                )}
              />
            </FormGroup>
            <FormGroup className="col-4" label="Grupa" errorMessage={errors.teamIds?.message} size="small">
              <Controller
                name="teamIds"
                control={control}
                rules={{ required: "Grupa este obligatorie" }}
                render={({ field }) => (
                  <TeamsDropdown
                    departmentId={watchedDepartmentId}
                    value={field.value[0]}
                    onChange={(selectedValue) => field.onChange([selectedValue])}
                    disabled={props.readOnly}
                  />
                )}
              />
            </FormGroup>
          </>
        )}
        <FormGroup className="col-4" label="Nume utilizator" size="small">
          <InputText disabled {...register("username")} />
        </FormGroup>
        <FormGroup className="col-4" label="Nume" size="small">
          <InputText disabled {...register("lastName")} />
        </FormGroup>
        <FormGroup className="col-4" label="Prenume" size="small">
          <InputText disabled {...register("firstName")} />
        </FormGroup>
        <FormGroup className="col-6" label="Email" size="small">
          <InputText disabled {...register("emailAddress")} />
        </FormGroup>
        <FormGroup className="col-6" label="Email secundar" size="small">
          <InputText disabled {...register("secondaryEmailAddress")} />
        </FormGroup>
        <FormGroup className="col-6" label="Numar telefon" size="small">
          <InputText disabled {...register("phoneNumber")} />
        </FormGroup>
        <FormGroup className="col-6" label="Numar mobil" size="small">
          <InputText disabled {...register("mobileNumber")} />
        </FormGroup>
        <FormGroup className="col-6" label="Manager" size="small">
          <Controller
            name="managerId"
            control={control}
            render={({ field }) => (
              <UsersDropdown
                placeholder="Selecteaza manager"
                disabled={props.readOnly}
                value={field.value}
                onChange={field.onChange}
              ></UsersDropdown>
            )}
          />
        </FormGroup>
        <FormGroup className="col-6" label="Companie" size="small">
          <InputText disabled {...register("company")} />
        </FormGroup>
        <FormGroup className="col-6" label="Locatie" size="small">
          <LocationsDropdown disabled value={props.user?.locationId} onChange={() => {}} />
        </FormGroup>
        <FormGroup className="col-6" label="Cod postal (IP)" size="small">
          <InputText disabled {...register("postalCode")} />
        </FormGroup>
      </div>
      {!props.readOnly && (
        <Button type="submit" className="block ml-auto" label="Salveaza" icon="pi pi-save" loading={isLoading} disabled={isLoading} />
      )}
    </form>
  );
}
